import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Typography
} from '@mui/material'
import { keyframes } from '@emotion/react'
import { ReactNode } from 'react'
import { MidasMessage } from 'api/midas'
import kingGlow2x from 'images/midas-king-glow-192.png'
import kingGlow from 'images/midas-king-glow-96.png'
import MarkdownLite from './MarkdownLite'

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
`

// Gold King Midas head with the pink glow baked in; not clipped to a circle.
export function MidasAvatar({ size = 32 }: Readonly<{ size?: number }>) {
  return (
    <Box
      component='img'
      src={kingGlow}
      srcSet={`${kingGlow} 1x, ${kingGlow2x} 2x`}
      alt=''
      data-testid='midas-avatar'
      sx={{ width: size, height: size, flexShrink: 0, objectFit: 'contain' }}
    />
  )
}

export function TypingIndicator() {
  return (
    <Stack
      direction='row'
      gap={0.5}
      alignItems='center'
      role='status'
      aria-label='Midas is typing'
      sx={{ py: 0.75 }}
    >
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: 'text.secondary',
            animation: `${blink} 1.2s ${i * 0.15}s infinite ease-in-out`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
          }}
        />
      ))}
    </Stack>
  )
}

function IncomingBubble({
  label,
  children
}: Readonly<{ label: 'Midas' | 'Goldie team'; children: ReactNode }>) {
  return (
    <Stack direction='row' gap={1} alignItems='flex-start' maxWidth='88%'>
      {label === 'Midas' ? (
        <MidasAvatar />
      ) : (
        <Avatar
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            fontSize: '0.875rem'
          }}
        >
          G
        </Avatar>
      )}
      <Box minWidth={0}>
        <Typography variant='caption' color='text.secondary' component='p'>
          {label}
        </Typography>
        {children}
      </Box>
    </Stack>
  )
}

const incomingSx = {
  bgcolor: '#F3F0EA',
  color: 'text.primary',
  borderRadius: '4px 16px 16px 16px',
  px: 1.5,
  py: 1,
  fontSize: '0.9375rem',
  lineHeight: 1.45,
  overflowWrap: 'anywhere'
} as const

interface MessageBubbleProps {
  message: MidasMessage
  onFeedback: (messageId: number, value: 1 | -1) => void
  onRetry?: () => void
}

export default function MessageBubble({
  message,
  onFeedback,
  onRetry
}: Readonly<MessageBubbleProps>) {
  const { author_type: author, body, status } = message

  if (author === 'system') {
    return (
      <Typography
        variant='caption'
        color='text.secondary'
        textAlign='center'
        component='p'
        sx={{ px: 3 }}
      >
        {body}
      </Typography>
    )
  }

  if (author === 'customer') {
    return (
      <Box
        alignSelf='flex-end'
        maxWidth='80%'
        sx={{
          bgcolor: 'primary.main',
          color: 'common.white',
          borderRadius: '16px 4px 16px 16px',
          px: 1.5,
          py: 1,
          fontSize: '0.9375rem',
          lineHeight: 1.45,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere'
        }}
      >
        {body}
      </Box>
    )
  }

  if (author === 'agent') {
    return (
      <IncomingBubble label='Goldie team'>
        <Box sx={incomingSx}>
          <MarkdownLite text={body} />
        </Box>
      </IncomingBubble>
    )
  }

  // Midas
  if (status === 'error') {
    return (
      <IncomingBubble label='Midas'>
        <Box sx={incomingSx} role='alert'>
          Sorry, I couldn&apos;t answer that just now.
          {onRetry && (
            <Button
              variant='text'
              size='small'
              onClick={onRetry}
              sx={{
                ml: 1,
                p: 0,
                height: 'auto',
                minWidth: 0,
                fontSize: '0.875rem',
                textTransform: 'none'
              }}
            >
              Try again
            </Button>
          )}
        </Box>
      </IncomingBubble>
    )
  }

  const complete = status === 'complete'
  return (
    <IncomingBubble label='Midas'>
      <Box sx={incomingSx}>
        {body === '' ? <TypingIndicator /> : <MarkdownLite text={body} />}
      </Box>
      {complete && body !== '' && (
        <Stack direction='row' gap={0.25} mt={0.25}>
          <IconButton
            size='small'
            aria-label='Helpful'
            aria-pressed={message.feedback === 1}
            onClick={() => onFeedback(message.id, 1)}
          >
            {message.feedback === 1 ? (
              <ThumbUpAltIcon fontSize='inherit' color='primary' />
            ) : (
              <ThumbUpAltOutlinedIcon fontSize='inherit' />
            )}
          </IconButton>
          <IconButton
            size='small'
            aria-label='Not helpful'
            aria-pressed={message.feedback === -1}
            onClick={() => onFeedback(message.id, -1)}
          >
            {message.feedback === -1 ? (
              <ThumbDownAltIcon fontSize='inherit' color='primary' />
            ) : (
              <ThumbDownAltOutlinedIcon fontSize='inherit' />
            )}
          </IconButton>
        </Stack>
      )}
    </IncomingBubble>
  )
}
